import type { ReactNode } from "react";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";

import { MobileBackButton } from "../misc/MobileBackButton";
import { MobileContent } from "./MobileContent";
import MobileHeader from "./MobileHeader";

export interface MobilePageProps {
  /** Already translated page title. */
  title: string;
  /** Where the back button goes; defaults to the list of the current resource. */
  backTo?: string;
  /** Icon buttons rendered on the right of the header. */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Chrome of a mobile detail or form page: the fixed header with a back
 * button, the page title and optional actions on the right, plus the
 * scrollable content area. The bottom navigation is rendered by
 * <MobileLayout>. Used by the phone variants of every create/edit page and
 * by the admin pages reached from the settings hub.
 */
export const MobilePage = ({
  title,
  backTo,
  actions,
  children,
}: MobilePageProps) => (
  <>
    <MobileHeader>
      <MobileBackButton to={backTo} />
      <div className="flex min-w-0 flex-1">
        <h1 className="truncate text-xl font-semibold text-brand-navy">
          {title}
        </h1>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-row items-center gap-1">
          {actions}
        </div>
      ) : null}
    </MobileHeader>
    <MobileContent>{children}</MobileContent>
  </>
);

/**
 * Save and cancel of a form inside a <MobilePage>: touch-sized buttons with
 * the primary action taking the remaining width. Unlike the desktop
 * <FormToolbar> it does not stick to the bottom of the viewport, which on a
 * phone is covered by the bottom navigation. Pass children to replace the
 * default pair.
 */
export const MobileFormToolbar = ({ children }: { children?: ReactNode }) => (
  <div
    role="toolbar"
    className="flex flex-row items-center gap-2 pt-2 [&>*]:h-11 [&>*]:text-base"
  >
    {children ?? (
      <>
        <CancelButton />
        <SaveButton className="flex-1" />
      </>
    )}
  </div>
);
